/**
 * Authentication Context
 * Provides Firebase authentication state and methods throughout the app
 * 
 * SAFETY GUARDS (Dec 2024):
 * - Auth readiness check before popup
 * - Centralized Google sign-in (single source of truth)
 * - Production-safe logging (no PII)
 * - Feature flag controlled rollback
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
    sendEmailVerification,
  confirmPasswordReset,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider, collections } from '@/config/firebase';
import toast from 'react-hot-toast';
import logger from '../utils/logger.js';
import { runDevPermissionSmokeTest } from '@/utils/devPermissionCheck';
import { subscribeToPush } from '@/services/pushService';
import { isAdminEmail } from '@/config/adminConfig';
import { isLaunchGracePeriodActive, gracePeriodConfig } from '@/utils/gracePeriod';
import { generateNewLoginQuote, clearLoginQuote } from '@/services/loginQuoteService';
import { FEATURE_FLAGS } from '@/config/featureFlags';

// Debug logging - only in dev with VITE_DEBUG_AUTH=true
const DEBUG_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEBUG_AUTH === 'true';

/**
 * Production-safe auth logging (no PII)
 * Logs only: source, timestamp, success/failure, redirect target
 */
const logAuthEvent = (event, data = {}) => {
  const timestamp = new Date().toISOString();
  const logData = {
    event,
    timestamp,
    ...data,
  };
  
  // Development: full console output
  if (import.meta.env.DEV) {
    console.log('[AuthEvent]', logData);
  }
  
  // Production: silent structured logging for monitoring
  // This can be picked up by error tracking services
  if (import.meta.env.PROD && typeof window !== 'undefined') {
    // Store in sessionStorage for debugging (limited to 10 entries)
    try {
      const logs = JSON.parse(sessionStorage.getItem('finlist_auth_logs') || '[]');
      logs.push(logData);
      if (logs.length > 10) logs.shift();
      sessionStorage.setItem('finlist_auth_logs', JSON.stringify(logs));
    } catch (e) {
      // Silent fail - storage might be unavailable
    }
  }
};

/**
 * Authentication Context
 */
const AuthContext = createContext({
  // Auth state
  currentUser: null,
  user: null,
  userProfile: null,
  loading: true,
  isPremium: false,
  isAdmin: false,
  trialInfo: null,
  currency: 'GBP',
  comparisonCurrency: null, // Secondary currency for FX comparison
  
  // Auth methods
  signIn: async () => {},
  signUp: async () => {},
  signInWithGoogle: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
  updateUserProfile: async () => {},
  changePassword: async () => {},
  
  // Utility methods
  refreshUser: async () => {},
  refreshPremiumClaim: async () => {},
});

/**
 * Custom hook to use authentication context
 * @returns {Object} Authentication context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  /**
   * Build Firebase Action Code Settings for email actions
   * @param {string} redirectPath - Path to redirect after action completes
   */
  const getActionCodeSettings = useCallback((redirectPath = '/auth/reset-password') => {
    // Production domain - always use www.getfinlist.com for signup/auth emails
    const isProd = import.meta.env.PROD || (typeof window !== 'undefined' && window.location.hostname !== 'localhost');
    const baseUrl = isProd 
      ? 'https://www.getfinlist.com'
      : (import.meta.env.VITE_PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'));
    const url = new URL(redirectPath, baseUrl).toString();
    return {
      url,
      handleCodeInApp: true
      // You can also add dynamicLinkDomain if using Firebase Dynamic Links
    };
  }, []);

  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [isTrialing, setIsTrialing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [currency, setCurrency] = useState('GBP'); // User's preferred currency
  const [comparisonCurrency, setComparisonCurrency] = useState(null); // Secondary currency for FX comparison
  
  // Auth readiness state - tracks if Firebase Auth has been initialized
  const [isAuthReady, setIsAuthReady] = useState(false);
  
  // Auth operation lock - prevents concurrent auth operations
  const authOperationLockRef = useRef(false);
  const authOperationSourceRef = useRef(null);
  
  // Launch Grace Period: All features free until April 1, 2026
  // This is a TOP-LEVEL OVERRIDE - does not modify billing logic
  const isGracePeriod = isLaunchGracePeriodActive();
  
  // Computed: User has full access if:
  // 1. Grace period is active (TOP-LEVEL OVERRIDE), OR
  // 2. User is premium, admin, or in active trial (existing Billing 2.0 logic)
  const hasFullAccess = isGracePeriod || isPremium || isAdmin || isTrialing;

  /**
   * Create or update user profile in Firestore
   * @param {Object} user - Firebase user object
   * @param {Object} additionalData - Additional profile data
   */
  const createUserProfile = useCallback(async (user, additionalData = {}) => {
    if (!user) return null;

    try {
      const userRef = doc(db, collections.USERS, user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Create new user profile with 30-day trial
        // Trial starts from April 1, 2026 (post-launch) not from signup date
        const TRIAL_DAYS = 30;
        const TRIAL_START_DATE = new Date('2026-04-01T00:00:00.000Z').getTime();
        const trialStartFrom = Math.max(Date.now(), TRIAL_START_DATE); // Trial starts from launch date or now, whichever is later
        const trialEndTimestamp = trialStartFrom + (TRIAL_DAYS * 24 * 60 * 60 * 1000);
        
        const profileData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || additionalData.displayName || '',
          photoURL: user.photoURL || '',
          provider: user.providerData[0]?.providerId || 'email',
          emailVerified: user.emailVerified,
          currency: 'GBP', // Default currency
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          // Billing: 30-day trial for all new users
          billing: {
            trialEnd: trialEndTimestamp,
            status: 'trialing',
            createdAt: Date.now()
          },
          preferences: {
            theme: 'system',
            notifications: true,
            autoSuggestions: true
          },
          ...additionalData
        };

        await setDoc(userRef, profileData);
        logger.info('✅ User profile created with 30-day trial');
        logger.debug('Profile data:', { uid: user.uid, email: user.email, trialEnd: trialEndTimestamp });
        
        return profileData;
      } else {
        // Update existing profile
        const updateData = {
          lastLoginAt: serverTimestamp(),
          emailVerified: user.emailVerified,
          ...additionalData
        };

        await updateDoc(userRef, updateData);
        
        const existingData = userDoc.data();
        return { ...existingData, ...updateData };
      }
    } catch (error) {
      console.error('Error creating/updating user profile:', error);
      toast.error('Failed to sync user profile');
      return null;
    }
  }, []);

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   */
  const signIn = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Update user profile
      await createUserProfile(result.user);
      
      // Generate a fresh motivational quote for this login session
      generateNewLoginQuote();
      
      toast.success('Welcome back!');
      return result.user;
    } catch (error) {
      console.error('Sign in error:', error);
      
      let message = 'Failed to sign in';
      
      switch (error.code) {
        case 'auth/user-not-found':
          message = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          message = 'Incorrect password';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address';
          break;
        case 'auth/user-disabled':
          message = 'This account has been disabled';
          break;
        case 'auth/too-many-requests':
          message = 'Too many failed attempts. Please try again later';
          break;
        default:
          message = error.message || 'Failed to sign in';
      }
      
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [createUserProfile]);

  /**
   * Sign up with email and password
   * @param {string} email - User email  
   * @param {string} password - User password
   * @param {string} displayName - User display name
   * @param {Object} additionalData - Additional profile data (e.g., legal acceptance)
   */
  const signUp = useCallback(async (email, password, displayName = '', additionalData = {}) => {
    try {
      setLoading(true);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user display name if provided
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      
      // Create user profile in Firestore with additional data (includes legal acceptance)
      await createUserProfile(result.user, { displayName, ...additionalData });
      
      // Send verification email with action code settings
      try {
        const settings = getActionCodeSettings('/auth/verify-email');
        await sendEmailVerification(result.user, settings);
        toast.success('Verification email sent');
      } catch (e) {
        console.warn('Failed to send verification email', e);
      }
      
      // Generate a fresh motivational quote for this login session
      generateNewLoginQuote();
      
      toast.success('Account created successfully!');
      return result.user;
    } catch (error) {
      console.error('Sign up error:', error);
      
      let message = 'Failed to create account';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          message = 'An account with this email already exists';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address';
          break;
        case 'auth/weak-password':
          message = 'Password should be at least 6 characters';
          break;
        case 'auth/operation-not-allowed':
          message = 'Email/password accounts are not enabled';
          break;
        default:
          message = error.message || 'Failed to create account';
      }
      
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [createUserProfile]);

  /**
   * Sign in with Google
   * 
   * CANONICAL GOOGLE SIGN-IN HANDLER (Single Source of Truth)
   * All Google sign-in flows MUST use this function:
   * - Sign In page
   * - Sign Up page
   * - Get Started CTA
   * - Any future Google auth entry point
   * 
   * SAFETY GUARDS:
   * - Auth readiness check (prevents popup before Firebase ready)
   * - Operation lock (prevents concurrent popups)
   * - Production-safe logging
   * - Feature flag controlled
   * 
   * @param {Object} additionalData - Additional profile data (e.g., legal acceptance from signup page)
   * @param {string} source - Source of auth trigger for logging (e.g., 'signin', 'signup', 'cta')
   */
  const signInWithGoogle = useCallback(async (additionalData = {}, source = 'unknown') => {
    // SAFETY: Feature flag check
    if (FEATURE_FLAGS.ENABLE_SAFE_AUTH_FLOW) {
      // Guard 1: Auth readiness check
      if (!isAuthReady) {
        logAuthEvent('auth_blocked', { source, reason: 'auth_not_ready' });
        toast.error('Please wait, authentication is loading...');
        return null;
      }
      
      // Guard 2: Prevent concurrent auth operations
      if (authOperationLockRef.current) {
        logAuthEvent('auth_blocked', { 
          source, 
          reason: 'concurrent_operation',
          existingSource: authOperationSourceRef.current 
        });
        // Silent block - don't spam the user
        return null;
      }
      
      // Acquire lock
      authOperationLockRef.current = true;
      authOperationSourceRef.current = source;
    }
    
    logAuthEvent('auth_start', { source, method: 'google' });
    
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      
      // Create or update user profile with additional data (includes legal acceptance if from signup)
      await createUserProfile(result.user, additionalData);
      
      // Generate a fresh motivational quote for this login session
      generateNewLoginQuote();
      
      logAuthEvent('auth_success', { source, method: 'google', isNewUser: !result.user.metadata.lastSignInTime });
      
      toast.success('Welcome to FinList!');
      return result.user;
    } catch (error) {
      // Log error (no PII - just error code)
      logAuthEvent('auth_error', { source, method: 'google', errorCode: error.code });
      
      console.error('Google sign in error:', error);
      
      let message = 'Failed to sign in with Google';
      
      switch (error.code) {
        case 'auth/account-exists-with-different-credential':
          message = 'An account already exists with this email using a different sign-in method';
          break;
        case 'auth/cancelled-popup-request':
        case 'auth/popup-closed-by-user':
          message = 'Sign in was cancelled';
          break;
        case 'auth/popup-blocked':
          message = 'Sign in popup was blocked by your browser';
          break;
        default:
          message = error.message || 'Failed to sign in with Google';
      }
      
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
      // Release lock
      if (FEATURE_FLAGS.ENABLE_SAFE_AUTH_FLOW) {
        authOperationLockRef.current = false;
        authOperationSourceRef.current = null;
      }
    }
  }, [createUserProfile, isAuthReady]);

  /**
   * Sign out current user
   * FINLIST AUTH RULES: Clears all session data and route memory
   * Navigation to /welcome handled by component calling this function
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      
      // Clear any stored route memory (for clean session handling)
      try {
        sessionStorage.removeItem('finlist_last_route');
        localStorage.removeItem('finlist_last_route');
        sessionStorage.removeItem('finlist_auth_return_path');
        localStorage.removeItem('finlist_auth_return_path');
      } catch (e) {
        // Storage might be unavailable in some contexts
        console.warn('[AuthContext] Could not clear route storage:', e);
      }
      
      // Clear the login quote for this session
      clearLoginQuote();
      
      await signOut(auth);
      setUserProfile(null);
      setCurrency('GBP'); // Reset to default currency
      setComparisonCurrency(null); // Reset comparison currency
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send password reset email
   * @param {string} email - User email
   */
  const resetPassword = useCallback(async (email) => {
    try {
      const settings = getActionCodeSettings('/auth/reset-password');
      await sendPasswordResetEmail(auth, email, settings);
      toast.success('Password reset email sent');
    } catch (error) {
      console.error('Password reset error:', error);
      
      let message = 'Failed to send reset email';
      
      switch (error.code) {
        case 'auth/user-not-found':
          message = 'No account found with this email';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address';
          break;
        default:
          message = error.message || 'Failed to send reset email';
      }
      
      toast.error(message);
      throw error;
    }
  }, [getActionCodeSettings]);

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   */
  const updateUserProfile = useCallback(async (profileData) => {
    if (!currentUser) throw new Error('No user signed in');

    try {
      setLoading(true);
      
      // Update Firebase Auth profile if display name or photo URL changed
      const authUpdates = {};
      if (profileData.displayName !== undefined) {
        authUpdates.displayName = profileData.displayName;
      }
      if (profileData.photoURL !== undefined) {
        authUpdates.photoURL = profileData.photoURL;
      }
      
      if (Object.keys(authUpdates).length > 0) {
        await updateProfile(currentUser, authUpdates);
      }
      
      // Update Firestore profile
      const userRef = doc(db, collections.USERS, currentUser.uid);
      const updateData = {
        ...profileData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(userRef, updateData);
      
      // Update local profile state
      setUserProfile(prev => ({ ...prev, ...updateData }));
      
      // Update currency if it was changed
      if (profileData.currency) {
        if (DEBUG_AUTH) console.log('💰 Currency updated via updateUserProfile:', profileData.currency);
        setCurrency(profileData.currency);
      }
      
      // Update comparison currency if it was changed
      if (profileData.comparisonCurrency !== undefined) {
        if (DEBUG_AUTH) console.log('💱 Comparison currency updated via updateUserProfile:', profileData.comparisonCurrency);
        setComparisonCurrency(profileData.comparisonCurrency);
      }
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    if (!currentUser || !currentUser.email) {
      throw new Error('No user signed in');
    }

    try {
      setLoading(true);
      
      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, newPassword);
      
      toast.success('Password updated successfully');
    } catch (error) {
      console.error('Password change error:', error);
      
      let message = 'Failed to update password';
      
      switch (error.code) {
        case 'auth/wrong-password':
          message = 'Current password is incorrect';
          break;
        case 'auth/weak-password':
          message = 'New password should be at least 6 characters';
          break;
        case 'auth/requires-recent-login':
          message = 'Please sign in again to change your password';
          break;
        default:
          message = error.message || 'Failed to update password';
      }
      
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Refresh user profile data and Firebase Auth state
   */
  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return;

    try {
      // Reload Firebase Auth user to get updated emailVerified status
      await auth.currentUser.reload();
      // Update the currentUser state with the reloaded user
      setCurrentUser({ ...auth.currentUser });
      
      const userRef = doc(db, collections.USERS, auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile(userData);
        // Update currency when profile is refreshed
        const newCurrency = userData.currency || 'GBP';
        if (DEBUG_AUTH) console.log('🔄 Refreshing user profile - Currency:', newCurrency);
        setCurrency(newCurrency);
        // Update comparison currency (null means same as base currency)
        setComparisonCurrency(userData.comparisonCurrency || null);
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  }, []);

  /**
   * Auth state change listener
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setCurrentUser(user);
        
        if (user) {
          // Load user profile from Firestore
          const profileData = await createUserProfile(user);
          setUserProfile(profileData);
          // Set currency from user profile
          const userCurrency = profileData?.currency || 'GBP';
          if (DEBUG_AUTH) console.log('🔐 User logged in - Currency:', userCurrency, 'Profile:', profileData);
          setCurrency(userCurrency);
          // Set comparison currency from user profile (null means same as base currency)
          const userComparisonCurrency = profileData?.comparisonCurrency || null;
          if (DEBUG_AUTH) console.log('💱 User logged in - Comparison currency:', userComparisonCurrency);
          setComparisonCurrency(userComparisonCurrency);
          // Fetch latest ID token claims to derive premium status
          try {
            const tokenResult = await user.getIdTokenResult(true);
            const premium = Boolean(tokenResult?.claims?.premium);
            
            // Check if user is an admin by email (appifiyer@gmail.com only)
            const admin = isAdminEmail(user.email);
            
            // Developer override: bypass premium checks in development
            const devBypass = import.meta.env.VITE_DEV_BYPASS_PREMIUM === 'true';
            
            // Admin users ALWAYS have premium access
            setIsPremium(admin || devBypass || premium);
            setIsAdmin(admin);
            
            if (admin) {
              if (DEBUG_AUTH) console.log('👑 Admin user detected - Full access granted');
            }
            
            // Fetch trial info from billing status endpoint
            try {
              const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/billing/status`, {
                headers: {
                  'Authorization': `Bearer ${await user.getIdToken()}`,
                  'Content-Type': 'application/json'
                }
              });
              if (response.ok) {
                const data = await response.json();
                const trialActive = data.trialActive || (data.effectiveTrialEnd && Date.now() < data.effectiveTrialEnd);
                
                setTrialInfo({
                  trialEnd: data.trialEnd || null,
                  effectiveTrialEnd: data.effectiveTrialEnd || null,
                  status: data.status || null,
                  trialActive
                });
                
                // Set trialing state - user has full access during trial
                setIsTrialing(trialActive && !premium);
                
                if (DEBUG_AUTH) console.log('📅 Trial info:', { 
                  trialActive, 
                  premium, 
                  trialEnd: data.trialEnd,
                  status: data.status 
                });
              }
            } catch (e) {
              console.warn('Failed to fetch trial info', e);
              setTrialInfo(null);
              setIsTrialing(false);
            }
          } catch (e) {
            console.warn('Failed to fetch ID token claims', e);
            setIsPremium(false);
            setIsAdmin(false);
            setIsTrialing(false);
            setTrialInfo(null);
          }
          
          if (DEBUG_AUTH) console.log('🔐 User authenticated:', {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName
          });

          // Sync push subscription if notifications are granted
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            subscribeToPush().then(() => {
              if (DEBUG_AUTH) console.log('🔔 Push subscription synced for user');
            }).catch(err => {
              console.warn('Push subscription sync failed (non-fatal):', err);
            });
          }

          // DEV-ONLY: Run permission smoke test after successful authentication
          if (import.meta.env.DEV) {
            runDevPermissionSmokeTest(user.uid).catch(err => {
              console.warn('Permission smoke test failed (non-fatal):', err);
            });
          }
        } else {
          setUserProfile(null);
          setCurrency('GBP'); // Reset to default when logged out
          setIsPremium(false);
          setIsAdmin(false);
          setIsTrialing(false);
          setTrialInfo(null);
          if (DEBUG_AUTH) console.log('🔓 User signed out');
        }
      } catch (error) {
        console.error('Auth state change error:', error);
      } finally {
        setLoading(false);
        // Mark auth as ready - Firebase Auth has completed initial check
        setIsAuthReady(true);
        logAuthEvent('auth_ready', { hasUser: !!currentUser });
      }
    });

    return unsubscribe;
  }, [createUserProfile]);

  /**
   * Force-refresh ID token to update premium claim locally
   */
  const refreshPremiumClaim = useCallback(async () => {
    if (!auth.currentUser) return false;
    try {
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const premium = Boolean(tokenResult?.claims?.premium);
      
      // Check if user is an admin by email (appifiyer@gmail.com only)
      const admin = isAdminEmail(auth.currentUser.email);
      
      // Admin users ALWAYS have premium access
      setIsPremium(admin || premium);
      setIsAdmin(admin);
      return admin || premium;
    } catch (e) {
      console.warn('Failed to refresh premium claim', e);
      setIsAdmin(false);
      return false;
    }
  }, []);

  /**
   * Context value
   */
  const value = {
    // State
    currentUser,
    user: currentUser,
    userProfile,
    loading,
    isAuthReady, // Auth readiness state - true once Firebase Auth initialized
    isPremium,
    isTrialing,
    hasFullAccess, // true if grace period active, premium, admin, or in trial
    isAdmin,
    trialInfo,
    currency, // User's preferred base currency
    comparisonCurrency, // Secondary currency for FX comparison (null = same as base)
    
    // Launch Grace Period (free until April 1, 2026)
    isGracePeriod, // true if grace period is active
    gracePeriodConfig, // config object with message, end date, etc.
    
    // Methods
    signIn,
    signUp,
    signInWithGoogle,
    logout,
    resetPassword,
    // Expose helpers used by ResetPasswordPage
    getActionCodeSettings,
      sendVerificationEmail: async () => {
        if (!auth.currentUser) throw new Error('No user signed in');
        try {
          console.log('📧 Attempting to send verification email to:', auth.currentUser.email);
          // Send without action code settings - simpler and more reliable
          await sendEmailVerification(auth.currentUser);
          console.log('📧 Verification email sent successfully');
          toast.success('Verification email sent! Check your inbox (and spam folder)');
        } catch (e) {
          console.error('📧 Verification email error:', e.code, e.message);
          // Provide user-friendly error messages
          if (e.code === 'auth/too-many-requests') {
            toast.error('Too many requests. Please wait a few minutes before trying again.');
          } else if (e.code === 'auth/invalid-email') {
            toast.error('Invalid email address.');
          } else {
            toast.error(`Failed to send: ${e.message}`);
          }
          throw e;
        }
      },
    updateUserProfile,
    changePassword,
    refreshUser,
    refreshPremiumClaim
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;